// attacks-info.ts — client-side render of the per-turn attack-budget breakdown
// panel ("Total Unhindered Attacks per Turn"), reading from window.FateTide.casterCache
// (no DB ping on a cache hit).
//
// Data: caster.computed.creature.attacks.per_turn = {total, base, sources, breakdown}
// compiled by compute_attacks() ([[project_compute_attacks_cluster]]) — Base attack
// (1) + extra_attack contributions (host / carriers / status / passive / inventory,
// inventory non-stacking cap applied). Mirrors the movement/sense panel: tinted
// header + the full source breakdown with a compiled Total footer.
//
// NOTE: unhindered_attack is a separate per-ability flag (an unhindered attack does
// not spend from this budget at runtime), NOT a contributor — see the footer note.

import { renderSourceLineBlock } from './source-line-block';
import { setActiveInfoPanel, paintActiveInfoPanel } from './active-panel';
import type { CachedCaster, SourceRow, AcPart } from './types';

function num(v: unknown): number {
    const n = Number(Array.isArray(v) ? v[0] : v);
    return Number.isFinite(n) ? n : 0;
}
function partTotal(part: AcPart | number | undefined): number {
    if (part === undefined || part === null) return 0;
    return (typeof part === 'object') ? num(part.total) : num(part);
}
function partSources(part: AcPart | number | undefined): SourceRow[] {
    return (part && typeof part === 'object' && Array.isArray(part.sources)) ? part.sources : [];
}

function renderAttacksPanel(caster: CachedCaster): string {
    const entry = ((caster.computed?.creature?.attacks as Record<string, AcPart | number> | undefined) ?? {}).per_turn;
    const total = partTotal(entry) || 1; // base attack is always at least 1
    // The innate "Base attack" row belongs to the host creature, so it should render
    // the `host` icon. Fresh compiles already stamp kind:'host' (compute_attacks.php),
    // but remap here too so creatures still holding a cached kind:'base' blob render
    // correctly without a recompile. Scoped to this panel — the shared source-line-block
    // keeps kind:'base' meaningful for other clusters (e.g. the AC base-10 row).
    const sources = partSources(entry).map(
        (s) => (s.kind === 'base' ? { ...s, kind: 'host' } : s),
    );

    let html = `<table class="table border mb-2">
        <thead><tr><th scope="col" colspan="2" class="table-danger align-middle">${iconMap('unhindered_attacks')} Total Unhindered Attacks per Turn</th></tr></thead>
    </table>`;

    // Full breakdown — Base attack + every extra_attack source, with the compiled Total
    // (icon + value) footer. With no sources cached yet this is just the Total row.
    html += renderSourceLineBlock(sources, 'unhindered_attacks', total, 'bg-secondary', caster);

    // Readable note (soft-blue, light text) on the dark side panel.
    html += `<div class="small rounded p-2 mt-2" role="note" style="color:#9ec5fe;background:rgba(110,168,254,0.10);border:1px solid rgba(110,168,254,0.28);">
        Attacks per turn before swings become <strong>hindered</strong>. Some abilities grant <strong>unhindered</strong> attacks that don't count against this.
    </div>`;

    return html;
}

/**
 * Entry point. Repointed onclick: the "Total Attacks per Turn" cell in
 * creature_summary_renderers.php (sideinfo + combat-extras strip).
 */
export async function renderAttacksInfoPanel(
    casterId: string,
    targetId = 'div_refresh_side_info',
): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target) {
        return;
    }

    let caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-muted">Loading…</div>';
        try {
            await fetchAndCacheCaster(casterId);
        } catch {
            // fetchAndCacheCaster swallows its own errors; fall through to the miss guard.
        }
        caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    }

    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-danger">Could not load creature data.</div>';
        return;
    }

    const render = (c: CachedCaster) => renderAttacksPanel(c);
    setActiveInfoPanel(casterId, targetId, render);
    paintActiveInfoPanel(caster);
}
